import type { Plugin } from '@rematch/core';
import { merge } from 'lodash';
import { Subject } from 'rxjs';

import type { connect } from '@swf/core/api/connect';
import { request } from '@swf/core/api/request';

import type { EffectMeta, EffectParams, RootState } from '../index';
import { select } from '../index';

type Connection = ReturnType<typeof connect> | undefined;

const createEffectPlugin = ({
  connection: conn,
  httpDomain,
  headers
}: {
  connection: Connection;
  httpDomain?: string;
  headers?: Record<string, string>;
}): Plugin => {
  const actionsObservable: EffectParams['actions'] = new Subject();
  const bagEffects = {};
  return {
    config: {
      redux: {
        middlewares: [
          (store) => (next) => (action) => {
            const result = next(action);
            actionsObservable.next({ type: action.type, state: store.getState() });
            return result;
          }
        ]
      }
    },
    onModel(namedModel, rematchStore) {
      const { name: modelName, effects } = namedModel;
      const { getState, dispatch } = rematchStore;
      if (!effects) {
        return;
      }
      if (typeof effects === 'function') {
        throw new Error(
          'Model effects must be plain objects, global dispatch is provided to each effect.'
        );
      }
      const bindRequest =
        (connection: Connection): EffectParams['request'] =>
        (options) => {
          const rootState = getState() as RootState;

          const { origin } = select.location.get(rootState);
          return request(
            merge(
              {
                httpDomain: httpDomain || origin,
                connection,
                headers
              },
              select.site.getRequestOptions(rootState),
              options
            )
          );
        };
      Object.entries(effects).forEach(([effectName, effect]) => {
        const key = `${modelName}/${effectName}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        bagEffects[key] = (payload: any, rootState: RootState, meta = {} as EffectMeta) =>
          effect(
            payload,
            {
              actions: actionsObservable,
              rootState,
              dispatch,
              request: bindRequest(meta.connection || conn)
            },
            meta
          );
      });
    },
    onStoreCreated(rematchStore, bag) {
      bag.effects = bagEffects;
    }
  };
};

export default createEffectPlugin;
